import { useState, useEffect, useMemo } from "react";
import type { CSSProperties } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import {
  listFormShares,
  createFormShareRequest,
  deleteFormShareRequest,
  fetchForms,
  fetchTeamDomains,
} from "../lib/api-client";
import type { FormShare, CustomDomain } from "../types/api";

type ShareType = FormShare["shareType"];

function FormSharingPage(): JSX.Element {
  const { teamId, formId } = useParams<{ teamId: string; formId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedFormId, setSelectedFormId] = useState<string | null>(formId ?? null);
  const [shareType, setShareType] = useState<ShareType>("public");
  const [password, setPassword] = useState("");
  const [expiryHours, setExpiryHours] = useState<number | null>(null);
  const [maxUses, setMaxUses] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null);

  // Fetch all forms in the team
  const formsQuery = useQuery({
    queryKey: ["forms", teamId],
    queryFn: () => fetchForms({ teamId }),
    enabled: Boolean(teamId),
  });

  // Filter for published forms only
  const publishedForms = useMemo(() => {
    if (!formsQuery.data?.items) return [];
    return formsQuery.data.items.filter(
      (form) => form.teamId === teamId && form.status === "published"
    );
  }, [formsQuery.data, teamId]);

  // Initialize selectedFormId from URL or first published form
  useEffect(() => {
    if (publishedForms.length === 0) {
      if (selectedFormId) {
        setSelectedFormId(null);
      }
      return;
    }

    // If URL formId exists and is in published forms, use it
    if (formId && publishedForms.some((f) => f.id === formId)) {
      if (selectedFormId !== formId) {
        setSelectedFormId(formId);
      }
      return;
    }

    // Otherwise, use first published form if current selection is invalid
    if (!selectedFormId || !publishedForms.some((f) => f.id === selectedFormId)) {
      setSelectedFormId(publishedForms[0]?.id ?? null);
    }
  }, [formId, publishedForms]);

  // Get selected form details
  const selectedForm = useMemo(() => {
    return publishedForms.find((f) => f.id === selectedFormId) ?? null;
  }, [publishedForms, selectedFormId]);

  // Update URL when selectedFormId changes (but not when it's initialized from URL)
  useEffect(() => {
    if (selectedFormId && selectedFormId !== formId && teamId && publishedForms.length > 0) {
      navigate(`/teams/${teamId}/forms/${selectedFormId}/sharing`, { replace: true });
    }
  }, [selectedFormId, formId, teamId, navigate, publishedForms.length]);

  const sharesQuery = useInfiniteQuery({
    queryKey: ["form-shares", selectedFormId],
    queryFn: ({ pageParam }) => listFormShares(selectedFormId!, { cursor: pageParam as string | undefined, limit: 10 }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(selectedFormId),
  });

  const createShareMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFormId) throw new Error("formId missing");
      return createFormShareRequest(selectedFormId, {
        shareType,
        password: shareType === "password" ? password : undefined,
        expiresInHours: expiryHours ?? undefined,
        maxUses: maxUses ?? undefined,
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["form-shares", selectedFormId] });
      setPassword("");
      setExpiryHours(null);
      setMaxUses(null);
      setToast({ type: "success", message: "Share link created successfully." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to create share link.",
      });
    },
  });

  const deleteShareMutation = useMutation({
    mutationFn: async (shareId: string) => {
      if (!selectedFormId) throw new Error("formId missing");
      return deleteFormShareRequest(selectedFormId, shareId);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["form-shares", selectedFormId] });
      setToast({ type: "success", message: "Share link deleted." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to delete share link.",
      });
    },
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const shares = sharesQuery.data?.pages.flatMap((page) => page.shares) ?? [];

  // Fetch team domains for custom URL display
  const domainsQuery = useQuery({
    queryKey: ["team-domains", teamId],
    queryFn: () => fetchTeamDomains(teamId!),
    enabled: Boolean(teamId),
  });

  const domains = domainsQuery.data?.domains ?? [];

  const getDomainById = (domainId: string | null): CustomDomain | undefined => {
    if (!domainId) return undefined;
    return domains.find((d) => d.id === domainId);
  };

  const handleCopyLink = (share: FormShare) => {
    const shareUrl = getShareUrl(share);
    void navigator.clipboard.writeText(shareUrl);
    setCopiedShareId(share.id);
    setTimeout(() => setCopiedShareId(null), 2000);
  };

  const getShareUrl = (share: FormShare) => {
    const domain = getDomainById(share.domainId);
    if (domain && domain.status === "active") {
      return `https://${domain.domain}/share/${share.token}`;
    }
    return `${window.location.origin}/share/${share.token}`;
  };

  const containerStyle: CSSProperties = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "2rem",
  };

  const cardStyle: CSSProperties = {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-base)",
    padding: "1.5rem",
    marginBottom: "2rem",
  };

  const formGroupStyle: CSSProperties = {
    marginBottom: "1rem",
  };

  const labelStyle: CSSProperties = {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: 500,
    marginBottom: "0.5rem",
    color: "var(--color-text)",
  };

  const inputStyle: CSSProperties = {
    width: "100%",
    padding: "0.625rem 0.875rem",
    border: "1px solid var(--color-border)",
    borderRadius: "calc(var(--radius-base) - 2px)",
    fontSize: "0.875rem",
    fontFamily: "inherit",
  };

  const shareItemStyle: CSSProperties = {
    background: "var(--color-bg-solid)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-base)",
    padding: "1rem",
    marginBottom: "0.75rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
  };

  const handleFormSelect = (newFormId: string) => {
    setSelectedFormId(newFormId);
  };

  return (
    <div style={containerStyle}>
      <h1 style={{ fontSize: "1.75rem", fontWeight: 600, marginBottom: "2rem" }}>
        Form Sharing
      </h1>

      {/* Form Selector Section */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem" }}>
          Select Form
        </h2>

        {formsQuery.isLoading ? (
          <div style={{ color: "var(--color-text-muted)" }}>Loading forms...</div>
        ) : publishedForms.length === 0 ? (
          <div style={{ color: "var(--color-text-muted)" }}>
            No published forms available in this team. Only published forms can be shared.
          </div>
        ) : (
          <>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Published Forms</label>
              <select
                value={selectedFormId ?? ""}
                onChange={(e) => handleFormSelect(e.target.value)}
                style={inputStyle}
              >
                {publishedForms.map((form) => (
                  <option key={form.id} value={form.id}>
                    {form.title || "Untitled form"} {form.publishedVersionNumber ? `(v${form.publishedVersionNumber})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {selectedForm && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  background: "var(--color-bg-solid)",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                  <span
                    className="pill"
                    style={{
                      background: "var(--color-success)",
                      color: "white",
                      fontSize: "0.75rem",
                      padding: "0.25rem 0.5rem",
                    }}
                  >
                    PUBLISHED
                  </span>
                  <h3 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>
                    {selectedForm.title || "Untitled form"}
                  </h3>
                </div>
                <div style={{ fontSize: "0.875rem", color: "var(--color-text-muted)" }}>
                  {selectedForm.publishedVersionNumber && (
                    <span style={{ marginRight: "1rem" }}>Version {selectedForm.publishedVersionNumber}</span>
                  )}
                  <span>Last updated: {new Date(selectedForm.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Share Section */}
      {selectedFormId && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
            Create New Share Link
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.5rem" }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Share Type</label>
              <select
                value={shareType}
                onChange={(e) => setShareType(e.target.value as ShareType)}
                style={inputStyle}
              >
                <option value="public">Public</option>
                <option value="password">Password Protected</option>
                <option value="one_time">One-Time Use</option>
                <option value="embed">Embeddable</option>
              </select>
            </div>

            {shareType === "password" && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  style={inputStyle}
                />
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.5rem" }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Expiry (hours)</label>
              <input
                type="number"
                value={expiryHours ?? ""}
                onChange={(e) => setExpiryHours(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Leave empty for no expiry"
                style={inputStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Max Uses</label>
              <input
                type="number"
                value={maxUses ?? ""}
                onChange={(e) => setMaxUses(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Leave empty for unlimited"
                style={inputStyle}
              />
            </div>
          </div>

          <button
            type="button"
            className="button primary"
            onClick={() => createShareMutation.mutate()}
            disabled={createShareMutation.isPending || (shareType === "password" && !password)}
          >
            {createShareMutation.isPending ? "Creating..." : "Create Share Link"}
          </button>
        </div>
      )}

      {/* Shares List */}
      {selectedFormId && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
            Active Share Links ({shares.length})
          </h2>

          {sharesQuery.isLoading ? (
            <p style={{ color: "var(--color-text-muted)" }}>Loading share links...</p>
          ) : shares.length === 0 ? (
            <p style={{ color: "var(--color-text-muted)" }}>No share links created yet.</p>
          ) : (
            <div>
              {shares.map((share) => (
                <div key={share.id} style={shareItemStyle}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>
                      {share.shareType.replace("_", " ").toUpperCase()}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--color-text-muted)",
                        wordBreak: "break-all",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {getShareUrl(share)}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                      {share.usedCount}/{share.maxUses ?? "∞"} uses
                      {share.expiresAt && ` • Expires: ${new Date(share.expiresAt).toLocaleString()}`}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                    <button
                      type="button"
                      className="button ghost"
                      onClick={() => handleCopyLink(share)}
                      style={{ fontSize: "0.875rem" }}
                    >
                      {copiedShareId === share.id ? "✓ Copied" : "Copy"}
                    </button>
                    <button
                      type="button"
                      className="button ghost"
                      onClick={() => deleteShareMutation.mutate(share.id)}
                      disabled={deleteShareMutation.isPending}
                      style={{ fontSize: "0.875rem", color: "var(--color-critical)" }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pre-fill Link Generator */}
      {selectedFormId && shares.length > 0 && (
        <PreFillLinkGenerator
          formId={selectedFormId}
          shares={shares}
        />
      )}

      {/* Embed Code Section */}
      {selectedFormId && shares.some((s) => s.shareType === "embed") && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1.5rem" }}>
            Embed Code
          </h2>
          <p style={{ color: "var(--color-text-muted)", marginBottom: "1rem", fontSize: "0.875rem" }}>
            Copy and paste this code into your website to embed the form:
          </p>
          <pre
            style={{
              background: "var(--color-bg-solid)",
              padding: "1rem",
              borderRadius: "calc(var(--radius-base) - 2px)",
              overflow: "auto",
              fontSize: "0.75rem",
            }}
          >
            {`<iframe src="${window.location.origin}/embed/[SHARE_TOKEN]" width="100%" height="600" frameborder="0"></iframe>`}
          </pre>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            background: toast.type === "success" ? "var(--color-success)" : "var(--color-critical)",
            color: "white",
            padding: "1rem 1.5rem",
            borderRadius: "var(--radius-base)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 1001,
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

/**
 * Pre-fill Link Generator component
 * Allows users to create share links with pre-filled field values
 */
function PreFillLinkGenerator({
  formId,
  shares,
}: {
  formId: string;
  shares: FormShare[];
}): JSX.Element {
  const [selectedShareId, setSelectedShareId] = useState<string>(shares[0]?.id ?? "");
  const [prefillValues, setPrefillValues] = useState<Record<string, string>>({});
  const [generatedUrl, setGeneratedUrl] = useState<string>("");
  const [copied, setCopied] = useState(false);

  // Fetch form detail to get fields
  const formDetailQuery = useQuery({
    queryKey: ["form-detail", formId],
    queryFn: () => import("../lib/api-client").then(m => m.fetchFormDetail(formId)),
    enabled: Boolean(formId),
  });

  const schema = formDetailQuery.data?.publishedVersion?.schema ?? formDetailQuery.data?.draft?.schema;
  const fillableFields = useMemo(() => {
    if (!schema) return [];
    return schema.fields.filter((f: { type: string }) =>
      ["short_text", "long_text", "email", "phone", "name", "number", "dropdown", "multiple_choice"].includes(f.type)
    );
  }, [schema]);

  const selectedShare = shares.find(s => s.id === selectedShareId);

  useEffect(() => {
    if (!selectedShare) {
      setGeneratedUrl("");
      return;
    }

    const baseUrl = `${window.location.origin}/share/${selectedShare.token}`;
    const params = new URLSearchParams();

    Object.entries(prefillValues).forEach(([fieldId, value]) => {
      if (value.trim()) {
        params.set(`prefill_${fieldId}`, value);
      }
    });

    const query = params.toString();
    setGeneratedUrl(query ? `${baseUrl}?${query}` : baseUrl);
  }, [selectedShare, prefillValues]);

  const handleCopy = () => {
    void navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cardStyle: CSSProperties = {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-base)",
    padding: "1.5rem",
    marginBottom: "2rem",
  };

  const inputStyle: CSSProperties = {
    width: "100%",
    padding: "0.625rem 0.875rem",
    border: "1px solid var(--color-border)",
    borderRadius: "calc(var(--radius-base) - 2px)",
    fontSize: "0.875rem",
    fontFamily: "inherit",
  };

  const labelStyle: CSSProperties = {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 500,
    marginBottom: "0.25rem",
    color: "var(--color-text-muted)",
  };

  return (
    <div style={cardStyle}>
      <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem" }}>
        Pre-fill Link Generator
      </h2>
      <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem", fontSize: "0.875rem" }}>
        Create a link with pre-filled values. Users can still edit these values before submitting.
      </p>

      {/* Share Link Selector */}
      <div style={{ marginBottom: "1.5rem" }}>
        <label style={labelStyle}>Select Share Link</label>
        <select
          value={selectedShareId}
          onChange={(e) => setSelectedShareId(e.target.value)}
          style={inputStyle}
        >
          {shares.map((share) => (
            <option key={share.id} value={share.id}>
              {share.shareType.replace("_", " ").toUpperCase()} - {share.token.slice(0, 8)}...
            </option>
          ))}
        </select>
      </div>

      {/* Field Values */}
      {formDetailQuery.isLoading ? (
        <p style={{ color: "var(--color-text-muted)" }}>Loading fields...</p>
      ) : fillableFields.length === 0 ? (
        <p style={{ color: "var(--color-text-muted)" }}>No fillable fields found in this form.</p>
      ) : (
        <div style={{ display: "grid", gap: "1rem", marginBottom: "1.5rem" }}>
          {fillableFields.map((field: { id: string; title: string; type: string }) => (
            <div key={field.id}>
              <label style={labelStyle}>
                {field.title || "Untitled field"}
                <span style={{ color: "var(--color-text-muted)", marginLeft: "0.5rem" }}>
                  ({field.type})
                </span>
              </label>
              <input
                type={field.type === "number" ? "number" : "text"}
                placeholder={`Pre-fill value for ${field.title || field.id}`}
                value={prefillValues[field.id] ?? ""}
                onChange={(e) => setPrefillValues(prev => ({
                  ...prev,
                  [field.id]: e.target.value,
                }))}
                style={inputStyle}
              />
            </div>
          ))}
        </div>
      )}

      {/* Generated URL */}
      {generatedUrl && (
        <div>
          <label style={labelStyle}>Generated Pre-fill Link</label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              type="text"
              value={generatedUrl}
              readOnly
              style={{ ...inputStyle, flex: 1, background: "var(--color-bg-solid)" }}
            />
            <button
              type="button"
              className="button primary"
              onClick={handleCopy}
              style={{ whiteSpace: "nowrap" }}
            >
              {copied ? "✓ Copied" : "Copy Link"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormSharingPage;
